import React, { useCallback, useEffect, useRef } from 'react';
import Menu from '@tencent/dui/lib/components/Menu';
import Divider from '@tencent/dui/lib/components/Divider';
import {
    BATCH_ADD_ANIM_ICON,
    BATCH_DELETE_ANIM_ICON,
} from '@src/framework/frame/components/animation-editor/images/index';
import ReactDOM from 'react-dom';
import {
    BATCH_DELETE_ANIMATION_MENU_ID,
    BATCH_ANIMATION_ID,
    BATCH_ANIMATION_TEXT,
    BATCH_DELETE_ANIMATION_MENU_LIST,
    DEFAULT_DELETE_LIST,
    tdwVer3Type,
    toolbarDeleteAnimationValue,
    DELETE_SUCCESS,
} from './constants';
import MoreDeleteOption from './more-delete-option';
import BatchAddAnimation from './batch-add-animation';
import { ENTR_LIST, IAnimConfig } from '@src/slide/domain/ui-model/element-modeles/property/animation/constants';
import styles from './index.less';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import IProps from './index.interface';
import { IAnimationEditorService } from '@src/slide/application/service/animation-editor-manager/core/interface';
import { IMultiplyModel } from '@src/slide/domain/ui-model/element-modeles/Interface/multiply-model.interface';
import { ISlideDataModel } from '@tencent/slide-sdk-model';
import { EventCodeType, TDWActionType, TDWModuleType, TDWOperationType, TDWVer4Type } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { FIXED_KEYS_DOUBLE } from '../../schema/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import { getCacConfigByKey } from '@src/slide/infrastructure/module/dependent/config/tob/app-config';

const BatchHandleAnimation = (props: IProps) => {
    const {
        batchAnimationInfo,
        currentSlide,
        slideDataList,
        selectedModel,
        selectedSlideList,
        getAnimationEditorService,
        getEditorAnimPreviewManager,
        slideModelList,
        currentSlideIndex,
    } = props;
    const animEditInstance = useRef<IAnimationEditorService>();
    useEffect(() => {
        tdwReport();
        getAnimationEditorService()
            ?.promise()
            .then((service) => {
                animEditInstance.current = service;
            });
    }, []);

    const createDialogContainer = () => {
        const dialogContainer = document.createElement('div');
        const hideDialog = () => {
            ReactDOM.unmountComponentAtNode(dialogContainer);
        };
        return { dialogContainer, hideDialog };
    };

    // 批量添加动画组件入口
    const handleBatchAddAnimation = () => {
        const { dialogContainer, hideDialog } = createDialogContainer();
        const entrList = ENTR_LIST.reduce((prev, cur) => (prev as IAnimConfig[]).concat(cur.children), []);
        ReactDOM.render(
            <BatchAddAnimation
                slideDataList={slideDataList}
                selectedSlideList={selectedSlideList}
                currentSlide={currentSlide}
                getAnimationEditorService={getAnimationEditorService}
                onClose={hideDialog}
                entrList={entrList}
                dialogVisible={true}
                getEditorAnimPreviewManager={getEditorAnimPreviewManager}
                slideModelList={slideModelList}
                currentSlideIndex={currentSlideIndex}
            />,
            dialogContainer,
        );
    };

    // 删除所有页动画
    const handleDeleteAllPageAnimation = () => {
        if (!animEditInstance.current) return;
        animEditInstance.current.deleteSelectedSlideAnimation({
            slideList: slideDataList,
            deletePresetClassList: DEFAULT_DELETE_LIST,
        });
        showDeleteSuccessMessage();
        toolbarDeleteTdwReport(toolbarDeleteAnimationValue.ALL_SLIDE);
    };

    // 删除选中元素动画
    const handleDeleteSelectedElementAnimation = () => {
        if (!animEditInstance.current) return;
        const shapeModels = (selectedModel as IMultiplyModel).selectedModels || [selectedModel];
        animEditInstance.current.deleteSelectedElementAnimation({
            shapeModels,
            deletePresetClassList: DEFAULT_DELETE_LIST,
        });
        showDeleteSuccessMessage();
        toolbarDeleteTdwReport(toolbarDeleteAnimationValue.SELECTED_ELEMENT);
    };

    // 删除选中页动画
    const handleDeleteSelectedPageAnimation = () => {
        const selectedSlides: ISlideModel[] = selectedSlideList.length ? selectedSlideList : [currentSlide];
        const slideList = selectedSlides.map(slideModel => slideModel.sdkData);
        if (!(slideList && animEditInstance?.current)) return;
        animEditInstance.current.deleteSelectedSlideAnimation({
            deletePresetClassList: DEFAULT_DELETE_LIST,
            slideList: slideList as ISlideDataModel[],
        });
        showDeleteSuccessMessage();
        toolbarDeleteTdwReport(toolbarDeleteAnimationValue.SELECTED_SLIDE);
    };

    // 更多删除选项组件入口
    const handleMoreDeleteOption = () => {
        const { dialogContainer, hideDialog } = createDialogContainer();
        ReactDOM.render(
            <MoreDeleteOption
                currentSlide={currentSlide}
                slideDataList={slideDataList}
                selectedModel={selectedModel}
                selectedSlideList={selectedSlideList}
                onClose={hideDialog}
                getAnimationEditorService={getAnimationEditorService}
                dialogVisible={true}
                showDeleteSuccessMessage={showDeleteSuccessMessage}
            />,
            dialogContainer,
        );
    };
    const handleClickMenu = useCallback((menuItemId: string) => {
        const onClickMenuItemMap = {
            [BATCH_ANIMATION_ID.BATCH_ADD_ANIMATION]: handleBatchAddAnimation,
            [BATCH_DELETE_ANIMATION_MENU_ID.DELETE_ALL_PAGE_ANIMATION]: handleDeleteAllPageAnimation,
            [BATCH_DELETE_ANIMATION_MENU_ID.DELETE_SELECTED_ELEMENT_ANIMATION]: handleDeleteSelectedElementAnimation,
            [BATCH_DELETE_ANIMATION_MENU_ID.DELETE_SELECTED_PAGE_ANIMATION]: handleDeleteSelectedPageAnimation,
            [BATCH_DELETE_ANIMATION_MENU_ID.MORE_DELETE_OPTION]: handleMoreDeleteOption,
        };
        const defaultHandler = () => {};
        (onClickMenuItemMap[menuItemId] ?? defaultHandler)();
        orderClkTdwReport(menuItemId);
    }, [
        handleBatchAddAnimation,
        handleDeleteAllPageAnimation,
        handleDeleteSelectedElementAnimation,
        handleDeleteSelectedPageAnimation,
        handleMoreDeleteOption,
    ]);

    // 批量动画-tdw上报
    const tdwReport = useCallback(() => {
        const reportInfo = {
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.ANIMATION_TAB,
            action: TDWActionType.BATCH,
            eventcode: EventCodeType.DOC_PPT_ANIMATION_TAB_BATCH,
        };
        const ver3 = batchAnimationInfo.path.includes(FIXED_KEYS_DOUBLE.TOOLBAR_ANIM)
            ? tdwVer3Type.TOOLBAR
            : tdwVer3Type.MENU;
        tdw({
            ...reportInfo,
            ver3,
            ver4: TDWVer4Type.TOOLBAR_NORMAL_DISPLAY,
        });
    }, [batchAnimationInfo.path]);

    // 批量动画-工具栏删除动画-tdw上报
    const toolbarDeleteTdwReport = useCallback((deleteRangeMap: number) => {
        const reportInfo = {
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.ANIMATION_BATCH,
            action: TDWActionType.TOOLBAR_DELETE,
            eventcode: EventCodeType.DOC_PPT_ANIMATION_BATCH_TOOLBAR_DELETE,
        };
        tdw({
            ...reportInfo,
            ver5: deleteRangeMap,
        });
    }, []);

    // 批量动画-菜单选项名-tdw上报
    const orderClkTdwReport = useCallback((menuItemId: string) => {
        const reportInfo = {
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.ANIMATION_BATCH,
            action: TDWActionType.ORDER_CLK,
            eventcode: EventCodeType.DOC_PPT_ANIMATION_BATCH_ORDER_CLK,
        };
        tdw({
            ...reportInfo,
            ver5: menuItemId,
        });
    }, []);

    // 删除成功弹窗
    const showDeleteSuccessMessage = useCallback(() => {
        Snackbar.show({
            message: DELETE_SUCCESS,
            type: 'success',
            duration: 3000,
        });
    }, []);
    return (
        <div>
            <Menu onClick={handleClickMenu} itemStyle={{ width: '175px' }} type="with-icon" selectable={false}>
                <Menu.Item
                    id={BATCH_ANIMATION_ID.BATCH_ADD_ANIMATION}
                    icon={BATCH_ADD_ANIM_ICON}
                    title={BATCH_ANIMATION_TEXT.BATCH_ADD_ANIMATION}
                    // 私有化隐藏限免图标
                    description={
                        getCacConfigByKey('custom.toolbarFileMenu.addAnimationFree', true)
                            ? <div
                                className={styles['add-animation-free']}
                            /> : <></>
                    }
                >
                    {BATCH_ANIMATION_TEXT.BATCH_ADD_ANIMATION}
                </Menu.Item>
                <Menu.SubMenu
                    id={BATCH_ANIMATION_ID.BATCH_DELETE_ANIMATION}
                    title={BATCH_ANIMATION_TEXT.BATCH_DELETE_ANIMATION}
                    icon={BATCH_DELETE_ANIM_ICON}
                >
                    {BATCH_DELETE_ANIMATION_MENU_LIST.map((menuConfig, index) => {
                        const elements = [
                            <Menu.Item id={menuConfig.id} key={menuConfig.id}>{menuConfig.text}</Menu.Item>,
                        ];
                        if (index === BATCH_DELETE_ANIMATION_MENU_LIST.length - 2) {
                            elements.push(<Divider key={`divider-${index}`} />);
                        }
                        return elements;
                    })}
                </Menu.SubMenu>
            </Menu>
        </div>
    );
};
export default BatchHandleAnimation;

